import {
  AgendaSlotType,
  BUSINESS_TIMEZONE,
  ImportAgendaSlotDoctolibCsvApiResponse,
  PeriodOfTheDay,
  UpdateAgendaSlotAssistantModeApiResponse,
} from '@omecare/utils';
import { differenceInCalendarDays, isBefore, startOfDay } from 'date-fns';
import { intlFormat } from 'date-fns/intlFormat';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookAgendaSlotAdminFormValues } from '@/components/forms/book-agenda-slot-admin/schema';
import { useBookAgendaSlotAdminForm } from '@/components/forms/book-agenda-slot-admin/use-book-agenda-slot-admin-form';
import { popModal, pushModal } from '@/components/modals';
import {
  ConfirmationModalViewModel,
  useConfirmModalViewModel,
} from '@/components/modals/confirmation-modal';
import useAgendaSlotExportDoctolibCsvMutation from '@/hooks/mutations/use-agenda-slot-export-doctolib-csv';
import {
  SetAccountingInformationResponse,
  useAgendaSlotSetAccountingInformation,
} from '@/hooks/mutations/use-agenda-slot-set-accounting-information';
import useAttachSlotToInvitation from '@/hooks/mutations/use-attach-slot-to-invitation';
import useBookAgendaSlotAdminMutation, {
  BookAgendaSlotAdminResponse,
} from '@/hooks/mutations/use-book-agenda-slot-admin';
import useConfirmAgendaSlotReservationMutation from '@/hooks/mutations/use-confirm-agenda-slot-reservation';
import useDeclineAgendaSlotReservationMutation from '@/hooks/mutations/use-decline-agenda-slot-reservation';
import useDeleteAgendaSlotMutation from '@/hooks/mutations/use-delete-agenda-slot';
import { DeleteOccurrenceAgendaSlotResponse } from '@/hooks/mutations/use-delete-occurrence-agenda-slot';
import useInviteDoctorSubstituteMutation from '@/hooks/mutations/use-invite-doctor-substitute';
import { StopRecurrenceAgendaSlotResponse } from '@/hooks/mutations/use-stop-recurrence-agenda-slot';
import { useUpdateAgendaSlotAssistantModeMutation } from '@/hooks/mutations/use-update-agenda-slot-assistant-mode';
import useImportAgendaSlotDoctolibCsvMutation from '@/hooks/mutations/use-update-agenda-slot-doctolib-csv';
import { useUpdateAgendaSlotReservationTagMutation } from '@/hooks/mutations/use-update-agenda-slot-reservation-tag';
import { useGetOfficeQuery } from '@/hooks/queries/use-get-office';
import {
  AgendaSlotItemReservation,
  GetAgendaSlotItemResponse,
} from '@/hooks/queries/use-get-office-agendas-slots';
import { doctorFullnameTitleTrad } from '@/utils/doctor';
import {
  EventOccurencePeriod,
  useEventOccurencePeriodInformation,
} from '@/utils/event-slot';
import { downloadBlob } from '@/utils/file';
import { getInitials } from '@/utils/get-initials';
import { SheetViewModel } from '../sheet.viewmodel';
import { getReservationStatus } from '../view-user-agenda-slot-sheet/view-user-agenda-slot-sheet.viewmodel';

export type ViewAdminAgendaSlotSheetApi = Partial<{
  onAfterDelete: () => void;
  onAfterBooked: (updatedSlot: BookAgendaSlotAdminResponse) => void;
  onAfterUpdated: (updatedSlot: Partial<GetAgendaSlotItemResponse>) => void;
  onAfterUpdateReservation: (
    reservation: AgendaSlotItemReservation | undefined,
  ) => void;
}>;

/**
 * Slot fields recomputed by the assistant-mode endpoint (SA override /
 * consultation mode toggles), as a patch for the local slot snapshot.
 *
 * @param response - the assistant-mode update response
 * @returns the slot attributes to merge into the cached slot
 */
const toAssistantModePatch = (
  response: UpdateAgendaSlotAssistantModeApiResponse,
): Pick<
  GetAgendaSlotItemResponse,
  | 'type'
  | 'withAssistant'
  | 'forceWithoutAssistant'
  | 'withAssistantInferred'
  | 'retrocessionRate'
> => {
  const { attributes } = response.data;
  return {
    type: attributes.type as AgendaSlotType,
    withAssistant: attributes.withAssistant,
    forceWithoutAssistant: attributes.forceWithoutAssistant,
    withAssistantInferred: attributes.withAssistantInferred,
    retrocessionRate: attributes.retrocessionRate,
  };
};

export type ViewAgendaSlotEventOccurrence = {
  id: string | null;
  allday: boolean;
  start: Date;
  end: Date | null;
  index: number;
  isOnLeave?: boolean;
};

const selectDeleteAgendaSlotClassic = ({
  slotId,
  sheetViewModel,
  onAfterDelete,
}: {
  slotId: string;
  sheetViewModel: SheetViewModel;
  onAfterDelete: ViewAdminAgendaSlotSheetApi['onAfterDelete'];
}) => {
  const {
    mutate: deleteAgendaSlot,
    isPending: deleting,
    isSuccess,
    isError,
  } = useDeleteAgendaSlotMutation();
  const { setModalViewModel } = useConfirmModalViewModel({
    isLoading: deleting,
    isSuccess,
    isError,
  });

  const closeDeleteAgendaSlot = () => {
    sheetViewModel.api.show();
  };

  const cancelDeleteAgendaSlot = () => {
    sheetViewModel.api.show();
  };

  const submitDeleteAgendaSlot = (id: string) => {
    deleteAgendaSlot(id);
  };

  useEffect(() => {
    if (isSuccess) {
      onAfterDelete?.();
      popModal('ConfirmationModal');
      sheetViewModel.api.close();
    }
  }, [isSuccess]);

  return {
    deleting,
    openModal: () => {
      pushModal('ConfirmationModal', {
        title: 'delete-agenda-slot-modal.title',
        description: 'delete-agenda-slot-modal.description',
        btnLabel: 'btn.delete',
        btnVariant: 'destructive',
        onConfirm: (viewModel: ConfirmationModalViewModel) => {
          setModalViewModel(viewModel);
          submitDeleteAgendaSlot(slotId);
        },
        onClose: closeDeleteAgendaSlot,
        onCancel: cancelDeleteAgendaSlot,
      });
    },
  };
};

const selectDeleteAgendaSlotRecurrence = ({
  slot,
  eventOccurrence,
  sheetViewModel,
  onAfterUpdated,
}: {
  slot: GetAgendaSlotItemResponse;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
  onAfterUpdated: ViewAdminAgendaSlotSheetApi['onAfterUpdated'];
}) => {
  return {
    openModal: () => {
      pushModal('DeleteAgendaSlotRecurrenceModal', {
        slot: {
          id: slot.id,
          rrule: slot.rrule!,
          exdate: slot.exdate,
        },
        eventOccurrence: {
          id: eventOccurrence.id,
          allday: eventOccurrence.allday,
          start: eventOccurrence.start,
          end: eventOccurrence.end,
          index: eventOccurrence.index,
        },
        onClose: (props?: { deleted: boolean }) => {
          if (props?.deleted) {
            return;
          }
          sheetViewModel.api.show();
        },
        onCancel: () => {
          sheetViewModel.api.show();
        },
        onDeleteOccurenceSuccess: ({
          data,
        }: {
          data: DeleteOccurrenceAgendaSlotResponse;
        }) => {
          sheetViewModel.api.close();

          onAfterUpdated?.({
            exdate: data.exdate,
          });
        },
        onStopSeriesSuccess: ({
          data,
        }: {
          data: StopRecurrenceAgendaSlotResponse;
        }) => {
          sheetViewModel.api.close();

          onAfterUpdated?.({
            rrule: data.rrule,
          });
        },
      });
    },
  };
};

const selectDeleteAgendaSlotState = ({
  slot,
  eventOccurrence,
  sheetViewModel,
  onAfterDelete,
  onAfterUpdated,
}: {
  slot: GetAgendaSlotItemResponse;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
  onAfterDelete: ViewAdminAgendaSlotSheetApi['onAfterDelete'];
  onAfterUpdated: ViewAdminAgendaSlotSheetApi['onAfterUpdated'];
}) => {
  const deleteAgendaSlotClassic = selectDeleteAgendaSlotClassic({
    slotId: slot.id,
    sheetViewModel,
    onAfterDelete,
  });
  const deleteAgendaSlotRecurrence = selectDeleteAgendaSlotRecurrence({
    slot,
    eventOccurrence,
    sheetViewModel,
    onAfterUpdated,
  });

  return {
    api: {
      open: () => {
        if (slot.rrule) {
          deleteAgendaSlotRecurrence.openModal();
        } else {
          deleteAgendaSlotClassic.openModal();
        }

        sheetViewModel.api.hide();
      },
    },
  };
};

const selectAdminBookSlotState = ({
  slotId,
  officeId,
  slotReccurrent,
  eventOccurrence,
  onAfterBooked,
  onAfterInvited,
  onAfterAttached,
}: {
  slotId: string;
  officeId: string;
  slotReccurrent: boolean;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
  onAfterBooked: ViewAdminAgendaSlotSheetApi['onAfterBooked'];
  onAfterInvited?: (invited: {
    firstName: string;
    lastName: string;
    email: string;
  }) => void;
  onAfterAttached?: () => void;
}) => {
  const {
    mutate: bookAgendaSlot,
    data: slotBookedResponse,
    isPending: bookLoading,
    isSuccess,
  } = useBookAgendaSlotAdminMutation();

  const { mutate: inviteDoctorSubstitute, isPending: inviteLoading } =
    useInviteDoctorSubstituteMutation();

  const { mutate: attachSlotToInvitation, isPending: attachLoading } =
    useAttachSlotToInvitation();

  const { bookAgendaSlotAdminForm } = useBookAgendaSlotAdminForm();

  const mode = bookAgendaSlotAdminForm.watch('mode') ?? 'select';

  useEffect(() => {
    if (isSuccess) {
      onAfterBooked?.(slotBookedResponse);
    }
  }, [isSuccess]);

  const submitBooking = (values: BookAgendaSlotAdminFormValues) => {
    if (values.mode === 'select' && values.invitationId) {
      attachSlotToInvitation(
        {
          invitationId: values.invitationId,
          slotId,
          officeId,
        },
        {
          onSuccess: () => {
            bookAgendaSlotAdminForm.reset({ mode: 'select' });
            onAfterAttached?.();
          },
        },
      );
      return;
    }
    if (values.mode === 'invite') {
      inviteDoctorSubstitute(
        {
          email: values.email ?? '',
          firstName: values.firstName ?? '',
          lastName: values.lastName ?? '',
          sitesAccess: [],
          slotId,
        },
        {
          onSuccess: () => {
            const invitedData = {
              firstName: values.firstName ?? '',
              lastName: values.lastName ?? '',
              email: values.email ?? '',
            };
            bookAgendaSlotAdminForm.reset({ mode: 'select' });
            onAfterInvited?.(invitedData);
          },
        },
      );
    } else {
      bookAgendaSlot({
        id: slotId,
        doctorSubstituteId: values.doctorSubstituteId!,
        tag: values.tag,
        occurrence: slotReccurrent
          ? {
              index: eventOccurrence.index,
              allday: eventOccurrence.allday,
              startAt: eventOccurrence.start,
              endAt: eventOccurrence.end,
            }
          : undefined,
      });
    }
  };

  return {
    api: {
      confirm: () => {
        bookAgendaSlotAdminForm.handleSubmit(submitBooking)();
      },
      confirmWithSubstitute: (doctorSubstituteId: string) => {
        bookAgendaSlotAdminForm.setValue(
          'doctorSubstituteId',
          doctorSubstituteId,
        );
        bookAgendaSlotAdminForm.setValue('mode', 'select');
        bookAgendaSlotAdminForm.handleSubmit(submitBooking)();
      },
    },
    data: {
      form: bookAgendaSlotAdminForm,
      loading: mode === 'invite' ? inviteLoading : attachLoading || bookLoading,
      mode,
    },
  };
};

const selectAgendaSlotReservationState = ({
  slot,
  sheetViewModel,
  setSlot,
  onAfterUpdateReservation,
  onAfterUpdated,
}: {
  slot: GetAgendaSlotItemResponse;
  sheetViewModel: SheetViewModel;
  setSlot: React.Dispatch<React.SetStateAction<GetAgendaSlotItemResponse>>;
  onAfterUpdateReservation: ViewAdminAgendaSlotSheetApi['onAfterUpdateReservation'];
  onAfterUpdated: ViewAdminAgendaSlotSheetApi['onAfterUpdated'];
}) => {
  const { t } = useTranslation();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [updateTagId, setUpdateTagId] = useState<string | null>(null);
  const { isPending: confirmLoading, mutate: confirmMutation } =
    useConfirmAgendaSlotReservationMutation();
  const { isPending: declineLoading, mutate: declineMutation } =
    useDeclineAgendaSlotReservationMutation();
  const { isPending: updateTagLoading, mutate: updateTagMutation } =
    useUpdateAgendaSlotReservationTagMutation();

  const reservations = slot.reservations ?? [];

  // Check if slot is in the past (date only)
  const slotDate = startOfDay(new Date(slot.startAt));
  const today = startOfDay(new Date());
  const isSlotInPast = isBefore(slotDate, today);

  if (reservations.length === 0) {
    return null;
  }

  return {
    reservations: reservations.map((res) => ({
      api: {
        move: () => {
          sheetViewModel.api.close();
          pushModal('MoveAgendaSlotModal', {
            sourceSlotId: slot.id,
            reservationId: res.id,
            onSuccess: () => {
              setSlot((prev) => {
                const updatedReservations = (prev.reservations ?? []).filter(
                  (r) => r.id !== res.id,
                );
                return {
                  ...prev,
                  reservations: updatedReservations,
                };
              });
              onAfterUpdated?.({ reservations: [] });
            },
          });
        },
        decline: () => {
          setDecliningId(res.id);
          declineMutation(res.id, {
            onSuccess: () => {
              setDecliningId(null);
              setSlot((prev) => {
                const updatedReservations = (prev.reservations ?? []).filter(
                  (r) => r.id !== res.id,
                );
                const remaining = updatedReservations[0];
                onAfterUpdateReservation?.(remaining ?? undefined);
                if (updatedReservations.length === 0) {
                  sheetViewModel.api.close();
                }
                return {
                  ...prev,
                  reservations: updatedReservations,
                };
              });
            },
            onError: () => setDecliningId(null),
          });
        },
        confirm: () => {
          const trad = doctorFullnameTitleTrad(res.doctorSubstitute);
          const fullname = t(trad.key, trad.params);

          sheetViewModel.api.close();

          pushModal('ConfirmReservationWithTagModal', {
            reservationId: res.id,
            doctorSubstituteName: fullname,
            onConfirm: ({ tag }: { tag?: string }) => {
              setConfirmingId(res.id);
              confirmMutation(
                { id: res.id, tag },
                {
                  onSuccess: (data) => {
                    setConfirmingId(null);
                    setSlot((prev) => ({
                      ...prev,
                      reservations: (prev.reservations ?? [])
                        .filter((r) => !data.autoDeclinedIds.includes(r.id))
                        .map((r) =>
                          r.id === res.id
                            ? { ...r, status: 'confirmed' as const, tag }
                            : r,
                        ),
                    }));
                    onAfterUpdateReservation?.({
                      ...res,
                      status: 'confirmed',
                      tag,
                    });
                  },
                  onError: () => setConfirmingId(null),
                },
              );
            },
          });
        },
        updateTag: (tag?: string) => {
          setUpdateTagId(res.id);
          updateTagMutation(
            { reservationId: res.id, tag },
            {
              onSuccess: () => {
                setUpdateTagId(null);
                setSlot((prev) => ({
                  ...prev,
                  reservations: (prev.reservations ?? []).map((r) =>
                    r.id === res.id ? { ...r, tag } : r,
                  ),
                }));
                onAfterUpdateReservation?.({ ...res, tag });
              },
              onError: () => setUpdateTagId(null),
            },
          );
        },
      },
      data: {
        id: res.id,
        doctorSubstitute: doctorFullnameTitleTrad(res.doctorSubstitute),
        doctorSubstituteInitials: getInitials(res.doctorSubstitute),
        doctorSubstitutePhoneNumber: res.doctorSubstitute.phoneNumber,
        doctorSubstituteRhythm: res.doctorSubstitute.rhythm,
        doctorSubstituteOnSiteSlotsWithoutAssistant:
          res.doctorSubstitute.onSiteSlotsWithoutAssistant,
        doctorSubstituteRemoteSlotsWithoutAssistant:
          res.doctorSubstitute.remoteSlotsWithoutAssistant,
        doctorSubstituteOnSiteSlotsWithAssistant:
          res.doctorSubstitute.onSiteSlotsWithAssistant,
        doctorSubstituteRemoteSlotsWithAssistant:
          res.doctorSubstitute.remoteSlotsWithAssistant,
        doctorSubstituteRhythmNote: res.doctorSubstitute.rhythmNote,
        doctorSubstitutePriority: res.doctorSubstitute.priority,
        doctorSubstituteInstallable: res.doctorSubstitute.installable,
        doctorSubstituteHasPhysicalSiteAccess:
          res.doctorSubstitute.hasPhysicalSiteAccess,
        status: {
          ...getReservationStatus({ reservation: res }),
          pending: res.status === 'pending',
          confirmed: res.status === 'confirmed',
        },
        confirmLoading: confirmLoading && confirmingId === res.id,
        declineLoading: declineLoading && decliningId === res.id,
        updateTagLoading: updateTagLoading && updateTagId === res.id,
        tag: res.tag,
        canMove: res.status === 'pending' && !isSlotInPast,
      },
    })),
    // First confirmed reservation (for doctolib import, accounting, etc.)
    confirmedReservation: reservations.find((r) => r.status === 'confirmed'),
  };
};

const selectDoctorlibImportCsvViewModel = ({
  slot,
  reservationConfirmed,
  onAfterImportSucceeded,
}: {
  slot: {
    id: string;
    periodOfTheDay: PeriodOfTheDay;
  };
  reservationConfirmed: boolean;
  onAfterImportSucceeded: (
    data: ImportAgendaSlotDoctolibCsvApiResponse,
  ) => void;
}) => {
  const importCsvMutation = useImportAgendaSlotDoctolibCsvMutation();
  return {
    enabled: reservationConfirmed && slot.periodOfTheDay !== 'allday',
    importing: importCsvMutation.isPending,
    api: {
      importCsv: ({ file }: { file: File }) => {
        importCsvMutation.mutate(
          {
            id: slot.id,
            doctolibFile: file,
          },
          {
            onSuccess: onAfterImportSucceeded,
          },
        );
      },
    },
  };
};

const selectDoctorlibExportCsvViewModel = ({
  slot,
  enabled,
}: {
  slot: {
    id: string;
    periodOfTheDay: PeriodOfTheDay;
    period: EventOccurencePeriod;
    doctorAttending: GetAgendaSlotItemResponse['doctorAttending'];
  };
  enabled: boolean;
}) => {
  const { t } = useTranslation();
  const exportCSVMutation = useAgendaSlotExportDoctolibCsvMutation();

  return {
    enabled,
    exporting: exportCSVMutation.isPending,
    api: {
      exportCsv: () => {
        const filename = `${t(
          'view-admin-agenda-slot-sheet.accounting.export-csv-data.filename',
          {
            doctorAttending: slot.doctorAttending.lastname,
            date: slot.period.singleDay.date,
            start: slot.period.singleDay.hours.start,
            end: slot.period.singleDay.hours.end,
          },
        )}.csv`;

        exportCSVMutation.mutate(
          {
            id: slot.id,
          },
          {
            onSuccess: (data) => {
              downloadBlob({
                filename,
                blob: data,
              });
            },
          },
        );
      },
    },
  };
};

const selectDoctorlibSetAccountingInformationViewModel = ({
  slot,
  reservationConfirmed,
  onSuccess,
}: {
  slot: {
    id: string;
    startAt: Date;
    hasAccountingData: boolean;
  };
  reservationConfirmed: boolean;
  onSuccess: (data: SetAccountingInformationResponse) => void;
}) => {
  const setAccountingInformationMutation =
    useAgendaSlotSetAccountingInformation();

  return {
    enabled:
      reservationConfirmed &&
      (slot.hasAccountingData ||
        differenceInCalendarDays(new Date(), slot.startAt) > 1),
    loading: setAccountingInformationMutation.isPending,
    api: {
      submit: () => {
        setAccountingInformationMutation.mutate(slot.id, {
          onSuccess,
        });
      },
    },
  };
};

const selectMoveSlotViewModel = ({
  slot,
  sheetViewModel,
  onAfterUpdated,
}: {
  slot: {
    id: string;
    startAt: Date;
    reservations: NonNullable<GetAgendaSlotItemResponse['reservations']>;
  };
  sheetViewModel: SheetViewModel;
  onAfterUpdated: ViewAdminAgendaSlotSheetApi['onAfterUpdated'];
}) => {
  // Check if slot is in the past (date only)
  const slotDate = startOfDay(new Date(slot.startAt));
  const today = startOfDay(new Date());
  const isSlotInPast = isBefore(slotDate, today);

  const reservationId = slot.reservations[0]?.id;

  return {
    enabled:
      slot.reservations.length > 0 &&
      !isSlotInPast &&
      !slot.reservations.some((r) => r.status === 'pending'),
    api: {
      open: () => {
        sheetViewModel.api.close();
        pushModal('MoveAgendaSlotModal', {
          sourceSlotId: slot.id,
          reservationId,
          onSuccess: () => {
            onAfterUpdated?.({ reservations: [] });
          },
        });
      },
    },
  };
};

export const selectViewAdminAgendaSlotSheetViewModel = ({
  slot: initialSlot,
  officeId,
  eventOccurrence,
  sheetViewModel,
  onAfterDelete,
  onAfterBooked,
  onAfterUpdated,
  onAfterUpdateReservation,
}: {
  slot: GetAgendaSlotItemResponse;
  officeId: string;
  eventOccurrence: ViewAgendaSlotEventOccurrence;
  sheetViewModel: SheetViewModel;
} & ViewAdminAgendaSlotSheetApi) => {
  const [slot, setSlot] = useState<GetAgendaSlotItemResponse>(initialSlot);
  const officeQuery = useGetOfficeQuery(officeId);

  const {
    isPending: updateAssistantModeLoading,
    mutate: updateAssistantModeMutation,
  } = useUpdateAgendaSlotAssistantModeMutation();

  const getSheetTitle = () => {
    return doctorFullnameTitleTrad(slot.doctorAttending);
  };

  const deleteSlot = selectDeleteAgendaSlotState({
    slot,
    eventOccurrence,
    sheetViewModel,
    onAfterDelete,
    onAfterUpdated,
  });
  const bookSlot = selectAdminBookSlotState({
    slotId: slot.id,
    officeId,
    slotReccurrent: !!slot.rrule,
    eventOccurrence,
    onAfterAttached: () => {
      onAfterUpdated?.({});
    },
    onAfterBooked: (updatedSlot: BookAgendaSlotAdminResponse) => {
      onAfterBooked?.(updatedSlot);
      setSlot((value) => {
        return {
          ...value,
          reservations: [
            ...(value.reservations ?? []),
            ...updatedSlot.reservations,
          ],
        };
      });
    },
    onAfterInvited: (invited) => {
      setSlot((value) => ({
        ...value,
        pendingInvitation: invited,
      }));
    },
  });

  const reservation = selectAgendaSlotReservationState({
    slot,
    sheetViewModel,
    setSlot,
    onAfterUpdateReservation: (
      reservation: AgendaSlotItemReservation | undefined,
    ) => {
      onAfterUpdateReservation?.(reservation);
    },
    onAfterUpdated: (updatedData) => {
      if (updatedData) {
        setSlot((value) => ({
          ...value,
          ...updatedData,
        }));
      }
      onAfterUpdated?.(updatedData);
    },
  });

  const period = useEventOccurencePeriodInformation({
    event: eventOccurrence,
  });

  const doctolibImport = selectDoctorlibImportCsvViewModel({
    slot: {
      id: slot.id,
      periodOfTheDay: slot.periodOfTheDay,
    },
    reservationConfirmed: !!reservation?.confirmedReservation,
    onAfterImportSucceeded: (updatedSlot) => {
      setSlot((value) => {
        return {
          ...value,
          morningDoctolibDataCreatedAt: updatedSlot.importedOn,
        };
      });
      onAfterUpdated?.({
        morningDoctolibDataCreatedAt: updatedSlot.importedOn,
      });
    },
  });

  const doctolibExport = selectDoctorlibExportCsvViewModel({
    slot: {
      id: slot.id,
      periodOfTheDay: slot.periodOfTheDay,
      period,
      doctorAttending: slot.doctorAttending,
    },
    enabled: !!slot.morningDoctolibDataCreatedAt,
  });

  const setAccountingInformation =
    selectDoctorlibSetAccountingInformationViewModel({
      slot: {
        id: slot.id,
        startAt: slot.startAt,
        hasAccountingData:
          slot.workAtTheSameTime !== null || slot.isFeeApplicable !== null,
      },
      reservationConfirmed: !!reservation?.confirmedReservation,
      onSuccess: (data) => {
        setSlot((value) => {
          return {
            ...value,
            isFeeApplicable: data.isFeeApplicable,
            workAtTheSameTime: data.workAtTheSameTime,
          };
        });
        onAfterUpdated?.({
          isFeeApplicable: data.isFeeApplicable,
          workAtTheSameTime: data.workAtTheSameTime,
        });
      },
    });

  const moveSlot = selectMoveSlotViewModel({
    slot: {
      id: slot.id,
      startAt: slot.startAt,
      reservations: slot.reservations ?? [],
    },
    sheetViewModel,
    onAfterUpdated: (updatedData: Partial<GetAgendaSlotItemResponse>) => {
      setSlot((value) => ({
        ...value,
        ...updatedData,
      }));
      onAfterUpdated?.(updatedData);
    },
  });

  // WorkAtTheSameTime by period
  let workAtTheSameTime:
    | {
        type: 'allday';
        value: {
          morning: number;
          afternoon: number;
        };
      }
    | {
        type: 'morning' | 'afternoon' | 'night';
        value: number;
      }
    | null = null;
  if (slot.workAtTheSameTime) {
    if (
      slot.periodOfTheDay === 'afternoon' ||
      slot.periodOfTheDay === 'morning' ||
      slot.periodOfTheDay === 'night'
    ) {
      workAtTheSameTime = {
        type: slot.periodOfTheDay,
        value: slot.workAtTheSameTime[slot.periodOfTheDay],
      };
    }

    if (slot.periodOfTheDay === 'allday') {
      workAtTheSameTime = {
        type: 'allday',
        value: slot.workAtTheSameTime,
      };
    }
  }

  // Both toggles recompute the effective assistant mode + rate server-side
  // (clearing the SA override brings the slot back to planning inference), so
  // reflect the authoritative values returned by the API rather than guessing
  // locally.
  const applyAssistantModeResponse = (
    response: UpdateAgendaSlotAssistantModeApiResponse,
  ) => {
    const updated = toAssistantModePatch(response);
    setSlot((value) => ({
      ...value,
      ...updated,
    }));
    onAfterUpdated?.(updated);
  };

  const updateForceWithoutAssistant = (forceWithoutAssistant: boolean) => {
    updateAssistantModeMutation(
      {
        agendaSlotId: slot.id,
        forceWithoutAssistant,
      },
      { onSuccess: applyAssistantModeResponse },
    );
  };

  const updateConsultationMode = (type: AgendaSlotType) => {
    updateAssistantModeMutation(
      {
        agendaSlotId: slot.id,
        type,
      },
      { onSuccess: applyAssistantModeResponse },
    );
  };

  return {
    title: getSheetTitle(),
    slotId: slot.id,
    api: {
      updateForceWithoutAssistant,
      updateConsultationMode,
    },
    updateAssistantModeLoading,
    information: {
      period,
      slotType: slot.type,
      startAt: slot.startAt,
      withAssistant: slot.withAssistant,
      forceWithoutAssistant: slot.forceWithoutAssistant ?? false,
      retrocessionRate: officeQuery.data
        ? slot.startAt.getDay() === 0 || slot.startAt.getDay() === 6
          ? officeQuery.data.weekendRetrocessionRate
          : slot.withAssistant
            ? officeQuery.data.withAssistantRetrocessionRate
            : officeQuery.data.withoutAssistantRetrocessionRate
        : slot.retrocessionRate,
      withAssistantInferred: slot.withAssistantInferred,
      periodOfTheDay: slot.periodOfTheDay,
      latestDoctolibImportAt: slot.morningDoctolibDataCreatedAt
        ? intlFormat(
            slot.morningDoctolibDataCreatedAt,
            {
              timeZone: BUSINESS_TIMEZONE,
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            },
            {
              locale: 'fr',
            },
          )
        : null,
      isFeeApplicable: slot.isFeeApplicable,
      workAtTheSameTime,
      reservationStatus: getReservationStatus({
        reservation: slot.reservations?.[0],
      }),
      updatedAt: intlFormat(
        slot.updatedAt,
        {
          timeZone: BUSINESS_TIMEZONE,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        },
        {
          locale: 'fr',
        },
      ),
    },
    officeId,
    doctolibImport,
    doctolibExport,
    pendingInvitation: slot.pendingInvitation,
    reservation,
    deleteSlot,
    bookSlot,
    setAccountingInformation,
    moveSlot,
  };
};
export type ViewAdminAgendaSlotSheetViewModel = ReturnType<
  typeof selectViewAdminAgendaSlotSheetViewModel
>;
